import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useState } from 'react';
import Media from 'react-media';

import LoadingSkeleton from '@controls/LoadingSkeleton';
import Popup from '@controls/Popup';

import { Button, colors, scale, useTheme } from '@scripts/gds';
import { useMedia } from '@scripts/hooks';

import FiltersIcon from '@icons/24/filters.svg';

import AnyFilterForm from './AnyFilterForm';
import { ProductFilterProps } from './types';

const FilterForm = dynamic(() => import('./FilterForm'));

const ProductFilter = ({
    isLoading = false,
    anyCollection = {
        isAnyCollection: false,
    },
    setProductDetailsData,
    preExpandedFilters,
    ...props
}: ProductFilterProps) => {
    const { query } = useRouter();
    const defaultMatches = query?.viewport ? query.viewport === 'desktop' : false;

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const { layout } = useTheme();
    const { md } = useMedia();

    return (
        <>
            <Media query={{ minWidth: layout?.breakpoints.md || 1024 }} defaultMatches={defaultMatches}>
                {matches =>
                    matches ? (
                        anyCollection.isAnyCollection ? (
                            <AnyFilterForm
                                isLoading={isLoading}
                                anyCollection={anyCollection}
                                preExpandedFilters={preExpandedFilters}
                                {...props}
                            ></AnyFilterForm>
                        ) : (
                            <FilterForm
                                isLoading={isLoading}
                                setProductDetailsData={setProductDetailsData}
                                {...props}
                            />
                        )
                    ) : (
                        <>
                            {!isLoading ? (
                                <>
                                    <Button
                                        data-testid="button-filter"
                                        onClick={() => setIsPopupOpen(true)}
                                        theme="outlineGrey"
                                        size="sm"
                                        block
                                        css={{
                                            position: 'relative',
                                            padding: 0,
                                            background: '#fff',
                                            border: 'none !important',
                                            '& > svg': {
                                                width: scale(3),
                                                height: scale(3),
                                                fill: colors.textMainBlack,
                                            },
                                            [md]: {
                                                height: scale(4),
                                                width: 'auto !important',
                                            },
                                        }}
                                    >
                                        <FiltersIcon />
                                    </Button>

                                    <Popup
                                        isSwipeable
                                        scrollInside
                                        isFullscreen
                                        isOpen={isPopupOpen}
                                        title="Фильтры"
                                        css={{ height: 'inherit' }}
                                        onRequestClose={() => setIsPopupOpen(false)}
                                    >
                                        <Popup.Body css={{ width: '100%' }}>
                                            {anyCollection.isAnyCollection ? (
                                                <AnyFilterForm
                                                    onPopupClose={() => setIsPopupOpen(false)}
                                                    isLoading={isLoading}
                                                    anyCollection={anyCollection}
                                                    preExpandedFilters={preExpandedFilters}
                                                    {...props}
                                                ></AnyFilterForm>
                                            ) : (
                                                <FilterForm
                                                    onPopupClose={() => setIsPopupOpen(false)}
                                                    isLoading={isLoading}
                                                    setProductDetailsData={setProductDetailsData}
                                                    {...props}
                                                />
                                            )}
                                        </Popup.Body>
                                    </Popup>
                                </>
                            ) : (
                                <LoadingSkeleton height={scale(6)} />
                            )}
                        </>
                    )
                }
            </Media>
        </>
    );
};

export default ProductFilter;
